import React, { Fragment, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import useWebsocket from "react-use-websocket";

import { useTenant } from "multitenancy/config";

import { usePermissions } from "hooks/permission";

import { useAppSelector } from "store/rootReducer";

import { CreateOitiva } from "features/cruds/Drawer/CreateOitiva";
import { CreateRecord } from "features/cruds/Drawer/CreateRecord";
import { LoadSpinner } from "features/LoadSpinner";

import { Hearing } from "pages/app/audiences/interface";

import { PageHeader } from "components/PageHeader";
import RecordCard from "components/RecordCard";
import SearchEmpty from "components/SearchEmpty";

import { hearingFileService } from "services/hearingFile";
import { recordersServices } from "services/recorders";

import { formatDateTime } from "utils/dateTime";
import { isExternalSourcePlatform } from "utils/entities/hearingFilePlatform";
import { FORMS_HEARING } from "utils/entities/permission";
import { useQuery } from "utils/get";

import { IListRecorded, WebSocketHearingResponse } from "./interface";
import { ListRecordedSkeleton } from "./ListRecordedSkeleton";
import { Container, Content } from "./styles";
import { statusRecorded, ticksToTime } from "../LeftList/util";

export const ListRecorded: React.FC<IListRecorded> = ({
  audienceId,
  placeId,
  selectedRecordId,
  setData,
  recordList,
  setSelectRecord,
  addToast,
  disabled,
  showAddButton = true,
  setLoading,
}) => {
  const { t } = useTranslation();
  const { productId } = useTenant();
  const [recordedList, setRecordedList] = useState<any[]>([]);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [date, setDate] = useState<string | null | undefined>();
  const [time, setTime] = useState<string | undefined>();
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [processingRecord, setProcessingRecord] = useState<Hearing>();

  const { user } = useAppSelector((state) => state.user);
  const isInqueritoProduct = productId === "inquerito";

  const { read, create } = usePermissions(FORMS_HEARING.HEARING_FILE);
  const query = useQuery();
  const RecordComponent = isInqueritoProduct ? CreateOitiva : CreateRecord;

  const canAddRecord = !!audienceId && showAddButton && create && !disabled;

  const handleAddClick = () => {
    if (isInqueritoProduct && !recordList.hearingAttendants?.length) {
      return addToast({
        type: "warning",
        title: t("common.warning"),
        description: t(
          "recordings.messages.cannotAddRecordingWithoutAttendants",
        ),
      });
    }
    setOpenDrawer(true);
  };

  const normalizeHearingFileItem = (item: any) => ({
    id: item.id ?? item.Id,
    hearingId: item.hearingId ?? item.HearingId,
    type: item.type ?? item.Type,
    hearingFileTypeId: item.hearingFileTypeId ?? item.HearingFileTypeId,
    confidential: item.confidential ?? item.Confidential,
    attendantId: item.attendantId ?? item.AttendantId,
    title: item.title ?? item.Title,
    filename: item.filename ?? item.Filename,
    recordId: item.recordId ?? item.RecordId,
    duration: item.duration ?? item.Duration,
    size: item.size ?? item.Size,
    fileOrder: item.fileOrder ?? item.FileOrder,
    hash: item.hash ?? item.Hash,
    createIn: item.createIn ?? item.CreateIn,
    updateIn: item.updateIn ?? item.UpdateIn,
    upload: item.upload ?? item.Upload,
    watermark: item.watermark ?? item.Watermark,
    platform: item.platform ?? item.Platform,
    status: item.status ?? item.Status,
    download: item.download ?? item.Download,
    expectedDate: item.expectedDate ?? item.ExpectedDate,
    startDate: item.startDate ?? item.StartDate,
    finishDate: item.finishDate ?? item.FinishDate,
    userId: item.userId ?? item.UserId,
    name: item.name ?? item.Name,
    role: item.role ?? item.Role,
  });

  const normalizeHearingAttendantItem = (item: any) => ({
    id: item.id ?? item.Id,
    name: item.name ?? item.Name,
    role: item.role ?? item.Role,
    attendantId: item.attendantId ?? item.AttendantId,
    attendantRoleId: item.attendantRoleId ?? item.AttendantRoleId,
    extraData: item.extraData ?? item.ExtraData,
    link: item.link ?? item.Link,
  });

  const normalizeRecordList = (data: any) => ({
    ...data,
    hearingScheduled: Array.isArray(data?.hearingScheduled)
      ? data.hearingScheduled.map(normalizeHearingFileItem)
      : Array.isArray(data?.HearingScheduled)
        ? data.HearingScheduled.map(normalizeHearingFileItem)
        : [],
    hearingInProgress: Array.isArray(data?.hearingInProgress)
      ? data.hearingInProgress.map(normalizeHearingFileItem)
      : Array.isArray(data?.HearingInProgress)
        ? data.HearingInProgress.map(normalizeHearingFileItem)
        : [],
    hearingAttendants: Array.isArray(data?.hearingAttendants)
      ? data.hearingAttendants.map(normalizeHearingAttendantItem)
      : Array.isArray(data?.HearingAttendants)
        ? data.HearingAttendants.map(normalizeHearingAttendantItem)
        : [],
  });

  useEffect(() => {
    (async () => {
      const hearingFileId = query.get("hearingFileId");
      setRecordedList([]);
      setListLoading(true);

      if (!audienceId || !placeId) {
        setListLoading(false);
        return;
      }

      try {
        const { data, success } = await recordersServices.getListByAudienceId({
          audienceId,
        });

        if (success) {
          const normalizedData = normalizeRecordList(data);
          if (setData) {
            setData(normalizedData);
          }
          setRecordedList(normalizedData.hearingInProgress);

          if (hearingFileId) {
            const hearingFileFinded: any =
              normalizedData?.hearingInProgress?.find(
                (x: any) => x.id === Number(hearingFileId),
              );

            if (hearingFileFinded && setSelectRecord)
              setSelectRecord(hearingFileFinded);
          }
        }
      } catch {
        // fetch failed silently — loading state still cleaned up below
      } finally {
        setListLoading(false);
      }
    })();
  }, [audienceId, placeId, openDrawer, query]);

  // console.log("recordList", recordList);

  const handleGetUrlByHearingFileId = async (item: any) => {
    setLoading(true);
    try {
      const url = await hearingFileService.getUrlById(item.id);

      item.filename = url.body.filename;

      if (setSelectRecord) {
        setSelectRecord(item);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  //#region web Socket

  const { accessToken } = useAppSelector((state) => state.auth);
  const WS_URL_HEARINGS = useMemo(
    () =>
      `${import.meta.env.VITE_APP_WSS_BASE_URL}/hearingFile?token=${accessToken}`,
    [accessToken],
  );
  const { sendMessage: sendMessageHearing } = useWebsocket(WS_URL_HEARINGS, {
    onMessage: (event: MessageEvent<any>) => {
      if (event.data) {
        const webSocketHearingFilesJson = JSON.parse(event.data);
        const webSocketHearingFiles =
          webSocketHearingFilesJson as WebSocketHearingResponse;

        if (webSocketHearingFiles.OK === true) {
          if (webSocketHearingFiles.Data) {
            const hearingInProgress =
              webSocketHearingFiles.Data.HearingInProgress;
            const hearingInProgressMap =
              hearingInProgressMapper(hearingInProgress);
            setRecordedList(hearingInProgressMap);

            if (setData) {
              //attendants
              const attendantsWebSocket =
                webSocketHearingFiles.Data.HearingAttendants;
              const attendantsWebSocketMap =
                hearingAttendantMapper(attendantsWebSocket);

              //schadelued
              const hearingScheduledWebSocket =
                webSocketHearingFiles.Data.HearingScheduled;
              const hearingScheduledWebSocketMap = hearingScheduledMapper(
                hearingScheduledWebSocket,
              );

              setData({
                ...recordList,
                hearingAttendants: attendantsWebSocketMap,
                hearingScheduled: hearingScheduledWebSocketMap,
              });
            }
          }
        }
      }
    },
    onOpen: () => {},
    onClose: () => {},
    shouldReconnect: () => true,
    reconnectInterval: 1000,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (audienceId && audienceId !== undefined && audienceId !== null) {
        sendMessageHearing(audienceId.toString());
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [audienceId]);

  useEffect(() => {
    if (!recordedList?.length) return;
    const currentItem = recordedList.find(
      (item) => item.id === selectedRecordId,
    );

    if (currentItem?.status !== 13) {
      setProcessingRecord(currentItem);
      return;
    }

    if (processingRecord) {
      handleGetUrlByHearingFileId(currentItem);
      setProcessingRecord(undefined);
    }
  }, [recordedList]);

  const hearingInProgressMapper = (hearingInProgress: any) => {
    return hearingInProgress.map((item: any) => normalizeHearingFileItem(item));
  };

  const hearingAttendantMapper = (attendants: any) => {
    return attendants?.map((item: any) => normalizeHearingAttendantItem(item));
  };

  const hearingScheduledMapper = (data: any) => {
    return data.map((item: any) => normalizeHearingFileItem(item));
  };

  //#endregion

  const checDisabledRecord = (record: any) => {
    if (isInqueritoProduct) {
      if (record.confidential && record.userId !== user?.id) {
        return true;
      }
    }
    return read ? disabled : true;
  };

  return (
    <Fragment>
      <Container>
        <PageHeader
          quantity={read ? recordedList?.length : 0}
          id="header"
          label={t("recordings.title")}
          background="secondary"
          addClick={canAddRecord ? handleAddClick : undefined}
          disabled={disabled}
        />

        {read ? (
          <Content>
            {listLoading ? (
              <ListRecordedSkeleton cardCount={5} />
            ) : recordedList?.length ? (
              recordedList.map((item, index) => (
                <RecordCard
                  key={index}
                  selected={item?.id === selectedRecordId}
                  duration={ticksToTime(item.duration)}
                  title={formatDateTime.getFullDate(item.startDate)}
                  subTitle={formatDateTime.getFullTime(item.startDate)}
                  labelStatus={statusRecorded(item.status).status}
                  type={statusRecorded(item.status).type}
                  isExternal={isExternalSourcePlatform(item.platform)}
                  onClick={
                    !disabled && setSelectRecord
                      ? () => handleGetUrlByHearingFileId(item)
                      : undefined
                  }
                  disabled={checDisabledRecord(item)}
                />
              ))
            ) : (
              <SearchEmpty
                textMessage={t("recordings.messages.noRecordings")}
                typeIcon="video"
                marginTop="40px"
              />
            )}
          </Content>
        ) : (
          <SearchEmpty
            textMessage={t("common.noPermissionForProfile")}
            typeIcon="avatar"
            marginTop="40px"
          />
        )}
      </Container>
      <RecordComponent
        addToast={addToast}
        title={t("recordings.singular").toLowerCase()}
        isOpen={create ? openDrawer : false}
        data={{
          hearingId: audienceId,
          userId: user?.id || 0,
        }}
        isEdit={false}
        setClose={() => setOpenDrawer(false)}
        date={date}
        setDate={setDate}
        time={time}
        setTime={setTime}
        customerId={user?.customerId}
      />
      {/* <LoadSpinner loading={loading} /> */}
    </Fragment>
  );
};
